import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import AuthService from '../services/authService';
import { ResetPasswordCredentials, User } from '../interfaces';
import { AnnonymousUser } from '../interfaces';

import 'react-toastify/dist/ReactToastify.css';

export interface AuthState {
  user: User | null;
  token: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  gotoResetPw: boolean;
  email: string;
}

const getInitialState = (): AuthState => ({
  user: null,
  token: null, // ✅ only runs when function is called
  status: 'idle',
  error: null,
  gotoResetPw: false,
  email: '',
});

const initialState = getInitialState();

export const signup = createAsyncThunk(
  'auth/signup',
  async (
    {
      emailAddress,
      firstName,
      lastName,
      phoneNumber,
      companyId,
      password,
    }: AnnonymousUser,
    { rejectWithValue }
  ) => {
    try {
      const data = await AuthService.signup({
        emailAddress,
        firstName,
        lastName,
        phoneNumber,
        companyId,
        password,
      });
      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.message || 'Error during signup');
      return rejectWithValue(error.message || 'Error during signup');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (
    { userName, password }: { userName: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await AuthService.login(userName, password);
      if (data.accessToken) {
        toast.success('Login successful!');
        return data;
      } else {
        toast.error(data.message || 'Login failed');
        return rejectWithValue(data.message);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.message || 'Error during login');
      return rejectWithValue(error.message || 'Error during login');
    }
  }
);

export const forgotPasswordAsync = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      const data = await AuthService.forgotPassword(email);
      toast.success('Password reset mail sent successful!');
      return { email, data };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // toast.error(error.message || "Error resetting password");
      return rejectWithValue(error.message || 'Error resetting password');
    }
  }
);

export const resetPasswordAsync = createAsyncThunk(
  'auth/resetPassword',
  async (
    {
      emailAddress,
      password,
      confirmPassword,
    }: { emailAddress: string; password: string; confirmPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await AuthService.resetPassword(
        emailAddress,
        password,
        confirmPassword
      );
      toast.success('Password reset successful!');
      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // toast.error(error.message || "Error resetting password");
      return rejectWithValue(error.message || 'Error resetting password');
    }
  }
);

export const tokenResetPasswordAsync = createAsyncThunk(
  'customer/reset-password',
  async ({
    email,
    password,
    confirmPassword,
    token,
  }: ResetPasswordCredentials) => {
    try {
      const data = await AuthService.tokenResetPassword({
        email,
        password,
        confirmPassword,
        token,
      });
      if (data.message) {
        if (data.succeded) {
          toast.success(data.message || 'Password Reset successful!');
        } else {
          toast.error(data.message || 'Password Reset failed!');
        }
        return data;
      } else {
        toast.error(data.error || 'Password Reset failed');
        return {
          succeded: false,
          message: 'Password Reset failed',
          data: null,
          responseCode: '60',
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.message || 'Error during password reset');
      return {
        succeded: false,
        message: 'Password Reset failed',
        data: null,
        responseCode: '60',
      };
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await AuthService.logout();
  toast.info('Logged out successfully');
  return null;
});

export const fetchUserInfo = createAsyncThunk(
  'auth/fetchUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const data = await AuthService.getUserInfo();
      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // toast.error(error.message || "Error fetching user info");
      return rejectWithValue(error.message || 'Error fetching user info');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    setEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    clearAuthState(state) {
      state.user = null;
      state.token = null;
      state.status = 'idle';
      state.error = null;
    },
    resetGoToReset(state) {
      state.gotoResetPw = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(tokenResetPasswordAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(tokenResetPasswordAsync.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(tokenResetPasswordAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(signup.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signup.fulfilled, (state) => {
        state.status = 'succeeded';
        // state.gotoResetPw = true

        // state.user = action.payload.data;
        // state.token = action.payload.data.accessToken;
        // AuthService.setAccessToken(action.payload.data.accessToken);
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(forgotPasswordAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(forgotPasswordAsync.fulfilled, (state) => {
        state.status = 'succeeded';
        state.gotoResetPw = true;
      })
      .addCase(forgotPasswordAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.accessToken;
        state.user = action.payload.user;
        AuthService.setAccessToken(action.payload.accessToken);
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'idle';
        state.error = null;
        AuthService.deleteAccessToken();
      })
      .addCase(fetchUserInfo.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.data;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const { setToken, clearAuthState, resetGoToReset, setEmail } =
  authSlice.actions;
export default authSlice.reducer;
